// ─── Edit this file to update prices, availability, or PayPal config ──────────
// It is the only file you need to touch for routine maintenance.

// Set to false to gray out and disable the Homepage Leaderboard option.
export const HOMEPAGE_BANNER_AVAILABLE = true;

// Pulled from the .env file. See .env.example for setup instructions.
export const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb';

// Ad-desk phone number shown across the form (footer, confirmation, EIN help).
export const CONTACT_PHONE = { display: '828-279-5740', tel: 'tel:+18282795740' };

// Print ad sizes — price is per weekly Wednesday run.
// specs: array of orientation objects. Single-entry = no orientation choice.
// format: required file format shown to the advertiser.
// pageEquivalent: fraction of a full page (used in order notifications / manifest).
// manifestLabel: label used in the Bookings Sheet / manifest (matches existing format).
// nonprofitPrice: discounted rate for verified 501(c)3 organizations.
//
// One-issue specials (optional fields — leave them off a regular size):
// nonprofitOnly: only non-profits see it; switching to business drops it.
// onlyIssue:     'YYYY-MM-DD' Wednesday it runs in, once. The run date is locked
//                to it, and the size hides itself when that issue closes.
// feature:       shows it as the highlighted card at the top of the size list.
//   pitch     — one line on why that issue matters.
//   specLine  — short file spec for the card.
//   files     — one PDF per entry; the "Send us your ad" card shows the file
//               names to use, e.g. YourOrg-LEFT.pdf and YourOrg-RIGHT.pdf.
//   sendNotes — extra lines on the "Send us your ad" card after ordering.
export const PRINT_ADS = [
  {
    id: 'two_page_spread',
    label: 'Two-Page Spread',
    price: 660,
    nonprofitPrice: 660,
    nonprofitOnly: true,
    onlyIssue: '2026-11-25',
    format: 'CMYK, one file per page',
    pageEquivalent: 2,
    specs: [{ orientation: null, dims: 'Two PDFs, 9.5" × 10.5" each', manifestLabel: '2-Page Spread' }],
    feature: {
      pitch:     'Two full pages side by side, in the Wednesday paper before Thanksgiving.',
      specLine:  'Two 9.5" × 10.5" CMYK PDFs',
      files:     ['LEFT', 'RIGHT'],
      sendNotes: ['Keep text and logos away from the inside edges, where the pages meet.'],
    },
  },
  {
    id: 'full_page',
    label: 'Full Page',
    price: 660,
    nonprofitPrice: 520,
    format: 'CMYK PDF or PNG',
    pageEquivalent: 1,
    specs: [{ orientation: null, dims: '9.5" × 10.5"', manifestLabel: 'Full Page' }],
  },
  {
    id: 'half_page',
    label: 'Half Page',
    price: 330,
    nonprofitPrice: 260,
    format: 'CMYK PDF or PNG',
    pageEquivalent: 0.5,
    specs: [
      { orientation: 'horizontal', label: 'Horizontal', dims: '9.5" × 5.2"',   manifestLabel: '1/2 Horizontal' },
      { orientation: 'vertical',   label: 'Vertical',   dims: '4.65" × 10.5"', manifestLabel: '1/2 Vertical'   },
    ],
  },
  {
    id: 'quarter_page',
    label: 'Quarter Page',
    price: 165,
    nonprofitPrice: 130,
    format: 'CMYK PDF or PNG',
    pageEquivalent: 0.25,
    specs: [{ orientation: null, dims: '4.65" × 5.2"', manifestLabel: '1/4 Square' }],
  },
  {
    id: 'eighth_page',
    label: 'Eighth Page',
    price: 82,
    nonprofitPrice: 65,
    format: 'CMYK PDF or PNG',
    pageEquivalent: 0.125,
    specs: [
      { orientation: 'horizontal', label: 'Horizontal', dims: '4.65" × 2.55"', manifestLabel: '1/8 Horizontal' },
      { orientation: 'vertical',   label: 'Vertical',   dims: '2.25" × 5.2"',  manifestLabel: '1/8 Vertical'   },
    ],
  },
  {
    id: 'sixteenth',
    label: 'Sixteenth / Classified',
    price: 41,
    nonprofitPrice: 40,
    format: 'CMYK PDF or PNG',
    pageEquivalent: 0.0625,
    specs: [{ orientation: null, dims: '2.25" × 2.55"', manifestLabel: '1/16 Square' }],
  },
];

// Digital placements.
// standalonePrice — when ordered without any print ad.
// addonPrice      — when bundled with any print ad.
// requiresAvailability — when true, respects the HOMEPAGE_BANNER_AVAILABLE manual toggle above.
// capacity — how many of this slot can run in a SINGLE week. The form reads the
//   Bookings Sheet (via the `availability` Netlify Function) and grays out a slot
//   that is already full for the advertiser's selected week(s). Set to null for
//   unlimited / untracked. Each booked week counts as one toward capacity.
// bookedThrough — optional 'YYYY-MM-DD'. Every Wednesday on or before this date is
//   treated as sold out (grayed out, with the usual skip/remove options), no matter
//   what the Bookings Sheet says. Weeks after it open back up on their own.
//   Set to null to turn it off.
export const DIGITAL_ADS = [
  {
    id: 'leaderboard',
    label: 'Homepage Leaderboard',
    dimensions: '728×90px',
    standalonePrice: 300,
    addonPrice: 250,
    nonprofitStandalonePrice: 250,
    nonprofitAddonPrice: 200,
    requiresAvailability: true,
    capacity: 1,            // run-of-site — one advertiser per week
    manifestLabel: 'WEB 728x90',
  },
  {
    id: 'square',
    label: 'Rotational Square',
    dimensions: '300×250px',
    standalonePrice: 150,
    addonPrice: 100,
    nonprofitStandalonePrice: 125,
    nonprofitAddonPrice: 100,
    requiresAvailability: false,
    capacity: 6,            // rotates among up to 6 advertisers per week
    manifestLabel: 'WEB 300x250',
  },
  {
    id: 'email_banner',
    label: 'Daily Email Newsletter Banner',
    dimensions: '728×90px',
    standalonePrice: 100,
    addonPrice: 75,
    nonprofitStandalonePrice: 100,
    nonprofitAddonPrice: 75,
    requiresAvailability: false,
    capacity: 3,            // up to 3 newsletter banners per week
    bookedThrough: '2026-11-03', // fully booked through Election Day
    manifestLabel: 'EMAIL 728x90',
  },
];

// Special Edition issues — add/remove entries here to keep the dropdown current.
// Dates must be Wednesdays in YYYY-MM-DD format. Ordering for these closes a
// week before the issue (see getOrderCutoff in utils/pricing.js).
// adDueDaysBefore (optional): when ad files are due, in days before the issue.
//   Leave it off for the usual Monday before (2). 5 = the Friday before.
export const SPECIAL_EDITIONS = [
  { date: '2026-05-20', name: 'Summer Guide'    },
  { date: '2026-09-02', name: 'Apple Festival'  },
  { date: '2026-10-14', name: 'Fall Voter Guide' },
  // Friday deadline: a big issue full of two-page spreads for production.
  { date: '2026-11-25', name: 'Season of Giving', adDueDaysBefore: 5 },
];

// Multi-week discount — a flat per-week amount off the whole order
// (print, digital, or both). Tiers are checked top-down; first match wins.
export const DISCOUNT_TIERS = [
  { minWeeks: 8, discountPerWeek: 20 },
  { minWeeks: 4, discountPerWeek: 10 },
  { minWeeks: 1, discountPerWeek: 0  },
];

// ─── Limited-time promo codes (flash / remnant sales) ─────────────────────────
// Link an advertiser to  …/?promo=flash25  and the matching entry below applies.
// A promo NEVER applies without the URL code — someone who walks up to the form
// and picks the same dates pays card rate.
//
// To run a new sale: copy an entry, change the values, deploy. To end one early:
// delete it. Otherwise `expires` shuts it off on its own.
//
//   code             — the ?promo= value (case-insensitive).
//   label            — shown in the banner and on the price-breakdown line.
//   blurb            — one line of banner copy.
//   discountPerWeek  — flat dollars off each week of the run. STACKS on top of
//                      the DISCOUNT_TIERS multi-week discount and on top of
//                      non-profit rates.
//   startDates       — the run must BEGIN on one of these Wednesdays.
//   eligiblePrintAds — PRINT_ADS ids the promo applies to. Digital never counts;
//                      an order with no qualifying print ad gets no discount.
//   expires          — last day the code works (inclusive). After this the link
//                      still loads the form, it just silently does nothing.
export const PROMOS = [
  {
    code:             'flash25',
    label:            'August Flash Sale',
    blurb:            '$25 off every week — quarter page or larger, Aug 12 or Aug 19.',
    discountPerWeek:  25,
    startDates:       ['2026-08-12', '2026-08-19'],
    eligiblePrintAds: ['quarter_page', 'half_page', 'full_page'],
    expires:          '2026-08-19',
  },
];
